/*
 * OnboardingTour — small floating "Help" bubble that opens a 4-step walkthrough.
 * Non-blocking; each step highlights a key dispatcher action.
 * Persists dismissal in localStorage so returning users don't see it re-opened.
 */
import React, { useEffect, useState } from "react";
import { HelpCircle, X, ChevronRight, ChevronLeft } from "lucide-react";

const STEPS = [
  {
    title: "1. Create a trip",
    body: "Click + New trip in the top-right. Pick pickup + drop-off from the address suggestions — the map, distance, and dead-mile calc all depend on validated coordinates.",
  },
  {
    title: "2. Assign a driver",
    body: "Click any trip row to see the closest drivers on the right (ranked by distance). Hit Assign to text the driver an SMS with a signed link that only works for their trip.",
  },
  {
    title: "3. Broadcast to multiple",
    body: "Not sure who is available? Broadcast to one TP, several TPs, or the whole network. First driver to accept wins — sibling links auto-revoke.",
  },
  {
    title: "4. Track live",
    body: "The map keeps their GPS position in real time (Beacon URL). Trip status updates every 6 seconds. Copy the driver's link or resend the SMS if they lost it.",
  },
];

const LS_KEY = "astat_tour_dismissed_v1";

export default function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(localStorage.getItem(LS_KEY) === "1");
  }, []);

  const startTour = () => { setStep(0); setOpen(true); };
  const close = () => setOpen(false);
  const finish = () => { localStorage.setItem(LS_KEY, "1"); setDismissed(true); setOpen(false); };

  // Auto-open once for first-time users
  useEffect(() => {
    if (!dismissed && !open) {
      const t = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(t);
    }
  }, [dismissed, open]);

  const s = STEPS[step];

  return (
    <>
      <button
        onClick={startTour}
        className="fixed bottom-6 right-6 z-[9998] w-12 h-12 rounded-full bg-forest text-white shadow-lg hover:brightness-95 grid place-items-center"
        title="Dispatcher walkthrough"
        data-testid="tour-open"
      >
        <HelpCircle size={22} />
      </button>
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[9999] w-[360px] bg-white rounded-2xl shadow-2xl border border-[#E9EAE4] p-5"
          data-testid="tour-popover"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
              Quick tour · {step + 1} of {STEPS.length}
            </div>
            <button onClick={close} className="text-slate-400 hover:text-slate-700" data-testid="tour-close">
              <X size={16} />
            </button>
          </div>
          <div className="font-bold text-slate-900 mb-2">{s.title}</div>
          <div className="text-sm text-slate-600 leading-relaxed mb-5">{s.body}</div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setStep((v) => Math.max(0, v - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-1 text-xs text-slate-500 disabled:opacity-30"
            >
              <ChevronLeft size={14} /> Back
            </button>
            <div className="flex gap-1">
              {STEPS.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-forest" : "bg-slate-200"}`} />
              ))}
            </div>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((v) => Math.min(STEPS.length - 1, v + 1))}
                className="inline-flex items-center gap-1 text-xs font-semibold text-forest"
                data-testid="tour-next"
              >
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={finish}
                className="text-xs font-semibold text-forest"
                data-testid="tour-finish"
              >
                Got it
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
